'use client';

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from '@kgc/i18n';
import { cn } from '@kgc/ui';
import { memo, Suspense, useCallback, useMemo, useState } from 'react';
import {
  useDashboardPreferences,
  useVisibleSections,
  useVisibleWidgets,
  type SectionId,
} from '../hooks';
import type { WidgetSize } from '../lib/widget-registry';
import { WIDGET_REGISTRY, WIDGET_SIZE_CLASSES } from '../lib/widget-registry';
import { CollapsibleSection } from './CollapsibleSection';
import { ExecutiveSummaryBar } from './ExecutiveSummaryBar';

export interface HybridDashboardLayoutProps {
  /** Optional className for container */
  className?: string;
}

/**
 * Widget loading skeleton with enhanced styling
 */
function WidgetSkeleton() {
  return (
    <div
      data-testid="widget-skeleton"
      className="flex items-center justify-center p-6 rounded-lg animate-pulse bg-white/50 dark:bg-slate-800/50"
    >
      <div className="h-32 bg-muted rounded w-full" />
    </div>
  );
}

/**
 * Widget theme class mapping based on category
 * Provides visual contrast and category identification
 * Epic 74: +kassza, analytics→rental with B+C color scheme
 */
const WIDGET_THEME_CLASS: Record<string, string> = {
  finance: 'widget-finance',
  kassza: 'widget-kassza',
  inventory: 'widget-inventory',
  service: 'widget-service',
  partner: 'widget-partner',
  rental: 'widget-rental',
  general: 'widget-enhanced',
  alerts: 'widget-enhanced',
};

/**
 * Render a single widget with Suspense boundary
 * Applies category-based theme class and size-aware grid span
 *
 * Performance: Wrapped in React.memo to prevent unnecessary re-renders
 * when parent components update but widget props remain the same.
 */
const WidgetRenderer = memo(function WidgetRenderer({ widgetId }: { widgetId: string }) {
  const config = WIDGET_REGISTRY[widgetId];

  if (!config) {
    return null;
  }

  const WidgetComponent = config.component;
  const themeClass = WIDGET_THEME_CLASS[config.category] || 'widget-enhanced';
  const sizeClass = WIDGET_SIZE_CLASSES[config.size as WidgetSize] || WIDGET_SIZE_CLASSES.m;

  return (
    <div className={cn(sizeClass, themeClass, 'rounded-xl p-4 transition-all duration-200')}>
      <Suspense fallback={<WidgetSkeleton />}>
        <WidgetComponent />
      </Suspense>
    </div>
  );
});

/**
 * Section content with widgets in 12-column grid (Epic 74)
 *
 * Performance: Wrapped in React.memo to prevent re-renders when
 * other sections are toggled or updated.
 */
const SectionWidgets = memo(function SectionWidgets({ sectionId }: { sectionId: SectionId }) {
  const { t } = useTranslation();
  const visibleWidgets = useVisibleWidgets(sectionId);

  if (visibleWidgets.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('dashboard.noWidgetsInSection')}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      {visibleWidgets.map(widget => (
        <WidgetRenderer key={widget.widgetId} widgetId={widget.widgetId} />
      ))}
    </div>
  );
});

/**
 * Sortable section wrapper for drag & drop (Epic 74 Story 74-6)
 */
function SortableSection({
  section,
  children,
  isDragDisabled,
}: {
  section: { sectionId: SectionId };
  children: React.ReactNode;
  isDragDisabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.sectionId,
    disabled: isDragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

/**
 * Hook to detect desktop viewport for DnD enabling
 */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 1024;
  });

  // Listen for resize
  useMemo(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isDesktop;
}

/**
 * Hybrid Dashboard Layout (Story 35-8, updated Epic 74)
 *
 * Implements the hybrid dashboard layout with:
 * - 12-column grid with variable widget sizes (XS/S/M/L/XL)
 * - B+C combined color scheme (left border + gradient glass)
 * - Kassza section with 5 cash register widgets
 * - Drag & drop section reordering (desktop only)
 * - Collapsible sections for each category
 * - Role-based widget filtering
 * - User preference persistence
 *
 * @see ADR-053 Dashboard Hibrid Layout Architektúra
 */
export function HybridDashboardLayout({ className }: HybridDashboardLayoutProps) {
  const { t, formatCurrency } = useTranslation();
  const sections = useVisibleSections();
  const isDesktop = useIsDesktop();
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const {
    isSectionExpanded,
    toggleSection,
    setExpandedSections,
    expandedSections,
    pinnedSections,
    isSectionPinned,
    togglePinSection,
    sectionOrder,
    moveSection,
    canMoveUp,
    canMoveDown,
    isLoading,
    resetToDefaults,
  } = useDashboardPreferences();

  // DnD sensors: pointer with 8px activation distance to avoid accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor)
  );

  // Filter out executive section (handled separately) and sections with no widgets
  // Sort by user-defined section order (persisted to localStorage)
  const collapsibleSections = useMemo(
    () =>
      sections
        .filter(section => section.sectionId !== 'executive' && section.isVisible)
        .sort((a, b) => {
          const aIndex = sectionOrder.indexOf(a.sectionId);
          const bIndex = sectionOrder.indexOf(b.sectionId);
          // Handle sections not in order array (put them at end)
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        }),
    [sections, sectionOrder]
  );

  const sectionIds = useMemo(
    () => collapsibleSections.map(s => s.sectionId),
    [collapsibleSections]
  );

  // Executive Summary visibility - uses 'executive' section pin state
  const isExecutiveVisible = !pinnedSections.includes('executive');
  const toggleExecutiveVisibility = useCallback(() => {
    togglePinSection('executive' as SectionId);
  }, [togglePinSection]);

  // Get all collapsible section IDs for expand/collapse all
  const allSectionIds = useMemo(
    () => collapsibleSections.map(s => s.sectionId),
    [collapsibleSections]
  );

  // Check if all sections are expanded or collapsed
  const allExpanded = useMemo(
    () => allSectionIds.every(id => expandedSections.includes(id)),
    [allSectionIds, expandedSections]
  );
  const allCollapsed = useMemo(
    () => allSectionIds.every(id => !expandedSections.includes(id)),
    [allSectionIds, expandedSections]
  );

  // Expand all sections
  const handleExpandAll = useCallback(() => {
    setExpandedSections(allSectionIds);
  }, [allSectionIds, setExpandedSections]);

  // Collapse all sections
  const handleCollapseAll = useCallback(() => {
    setExpandedSections([]);
  }, [setExpandedSections]);

  // DnD handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeId = active.id as SectionId;
      const overId = over.id as SectionId;
      const activeIndex = sectionOrder.indexOf(activeId);
      const overIndex = sectionOrder.indexOf(overId);

      if (activeIndex === -1 || overIndex === -1) return;

      // Determine direction and move step by step
      if (activeIndex < overIndex) {
        for (let i = 0; i < overIndex - activeIndex; i++) {
          moveSection(activeId, 'down');
        }
      } else {
        for (let i = 0; i < activeIndex - overIndex; i++) {
          moveSection(activeId, 'up');
        }
      }
    },
    [sectionOrder, moveSection]
  );

  // Executive section KPIs for the summary bar
  // TODO: Replace with real KPI data from API
  const executiveKpis = useMemo(
    () => [
      {
        id: 'health',
        label: t('dashboard.executive.businessHealth'),
        value: t('dashboard.executive.healthGood'),
        icon: '🌤️',
        status: 'healthy' as const,
      },
      {
        id: 'revenue',
        label: t('dashboard.executive.dailyRevenue'),
        value: formatCurrency(847500),
        trend: '+5%',
        subtext: t('dashboard.executive.revenueTarget', { amount: formatCurrency(1000000) }),
        icon: '💰',
        status: 'warning' as const,
      },
      {
        id: 'inventory',
        label: t('dashboard.executive.inventory'),
        value: '92%',
        trend: '±0%',
        subtext: t('dashboard.executive.utilization'),
        icon: '📦',
        status: 'healthy' as const,
      },
      {
        id: 'service',
        label: t('dashboard.executive.activeWorksheets'),
        value: '12 db',
        trend: '+3',
        subtext: t('dashboard.executive.openedToday'),
        icon: '🔧',
        status: 'healthy' as const,
      },
      {
        id: 'receivables',
        label: t('dashboard.executive.receivables'),
        value: formatCurrency(2450000),
        trend: '+12%',
        subtext: t('dashboard.executive.receivablesTarget', { amount: formatCurrency(2000000) }),
        icon: '⚠️',
        status: 'critical' as const,
      },
    ],
    [t, formatCurrency]
  );

  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="h-24 bg-muted animate-pulse rounded-lg" />
        <div className="h-48 bg-muted animate-pulse rounded-lg" />
        <div className="h-48 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  // Active section for drag overlay
  const activeSection = activeDragId
    ? collapsibleSections.find(s => s.sectionId === activeDragId)
    : null;

  return (
    <div className={cn('space-y-4', className)} data-testid="hybrid-dashboard-layout">
      {/* Executive Summary Bar - Toggleable visibility */}
      {isExecutiveVisible ? (
        <div className="relative">
          <ExecutiveSummaryBar kpiData={executiveKpis} />
          {/* Hide button */}
          <button
            data-testid="hide-executive-summary"
            type="button"
            onClick={toggleExecutiveVisibility}
            className={cn(
              'absolute top-2 right-2',
              'w-8 h-8 flex items-center justify-center',
              'rounded-md',
              'bg-card/80 hover:bg-card',
              'text-muted-foreground hover:text-foreground',
              'transition-colors',
              'border border-border/50'
            )}
            title={t('dashboard.hideExecutiveSummary')}
            aria-label={t('dashboard.hideExecutiveSummary')}
          >
            ✕
          </button>
        </div>
      ) : (
        <button
          data-testid="show-executive-summary"
          type="button"
          onClick={toggleExecutiveVisibility}
          className={cn(
            'w-full py-3 px-4',
            'flex items-center justify-center gap-2',
            'rounded-xl border-2 border-dashed border-primary/30',
            'bg-card/50 hover:bg-card',
            'text-muted-foreground hover:text-foreground',
            'transition-all duration-200'
          )}
        >
          <span>📊</span>
          <span className="text-sm font-medium">{t('dashboard.showExecutiveSummary')}</span>
          <span>▼</span>
        </button>
      )}

      {/* Section Controls Toolbar */}
      {collapsibleSections.length > 0 && (
        <div data-testid="section-controls" className="flex items-center justify-between px-2">
          <span className="text-sm text-muted-foreground">
            {t('dashboard.sectionCount', { count: collapsibleSections.length })}
          </span>
          <div className="flex items-center gap-2">
            {/* Reset to defaults button (Epic 74) */}
            <button
              data-testid="reset-layout-button"
              type="button"
              onClick={resetToDefaults}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md',
                'border border-border',
                'transition-colors',
                'bg-card hover:bg-accent text-foreground'
              )}
              title={t('dashboard.resetLayout')}
            >
              ↺
            </button>
            <button
              data-testid="expand-all-button"
              type="button"
              onClick={handleExpandAll}
              disabled={allExpanded}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md',
                'border border-border',
                'transition-colors',
                allExpanded
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-card hover:bg-accent text-foreground'
              )}
            >
              {t('dashboard.expandAll')}
            </button>
            <button
              data-testid="collapse-all-button"
              type="button"
              onClick={handleCollapseAll}
              disabled={allCollapsed}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md',
                'border border-border',
                'transition-colors',
                allCollapsed
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-card hover:bg-accent text-foreground'
              )}
            >
              {t('dashboard.collapseAll')}
            </button>
          </div>
        </div>
      )}

      {/* Collapsible Sections with Drag & Drop (Epic 74 Story 74-6) */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
          {collapsibleSections.map(section => (
            <SortableSection key={section.sectionId} section={section} isDragDisabled={!isDesktop}>
              <CollapsibleSection
                id={section.sectionId}
                title={t(section.titleKey)}
                icon={section.icon}
                widgetCount={section.visibleWidgetCount}
                alertCount={section.alertCount}
                expanded={isSectionExpanded(section.sectionId)}
                onExpandedChange={() => toggleSection(section.sectionId)}
                isPinned={isSectionPinned(section.sectionId)}
                onPinChange={() => togglePinSection(section.sectionId)}
                canMoveUp={canMoveUp(section.sectionId)}
                canMoveDown={canMoveDown(section.sectionId)}
                onMoveUp={() => moveSection(section.sectionId, 'up')}
                onMoveDown={() => moveSection(section.sectionId, 'down')}
              >
                <SectionWidgets sectionId={section.sectionId} />
              </CollapsibleSection>
            </SortableSection>
          ))}
        </SortableContext>

        {/* Drag overlay - shows ghost of section being dragged */}
        <DragOverlay>
          {activeSection ? (
            <div className="opacity-80 shadow-2xl rounded-lg scale-[1.02]">
              <CollapsibleSection
                id={activeSection.sectionId}
                title={t(activeSection.titleKey)}
                icon={activeSection.icon}
                widgetCount={activeSection.visibleWidgetCount}
                alertCount={activeSection.alertCount}
                expanded={false}
              >
                <div className="h-16 flex items-center justify-center text-muted-foreground text-sm">
                  {t('dashboard.dragToReorder')}
                </div>
              </CollapsibleSection>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Empty state if no sections visible */}
      {collapsibleSections.length === 0 && (
        <div data-testid="no-sections-message" className="text-center py-16 text-muted-foreground">
          <p className="text-lg">{t('dashboard.noSections')}</p>
          <p className="text-sm mt-2">{t('dashboard.contactAdminForPermissions')}</p>
        </div>
      )}
    </div>
  );
}
